/**
 * 6 · Prelievi da magazzino negozio.
 *
 * La merce che **non arriva da nessun ordine di acquisto**: è già a stock. Due
 * filoni — gli **express**, la cui merce c'è già tutta, e le righe mancanti
 * degli **ordini parziali**.
 *
 * La schermata esiste perché finché una di queste righe non è presa, l'ordine
 * **non entra in produzione** (regola 15). È il posto da cui si sblocca.
 *
 * Since 09/10/2026 an express order is also asked **by number**: DecoNetwork
 * has no field that says an order is express, so the plan never lists one. The
 * operator types the number, sees what the Modula holds of it, and one button
 * asks for what is there.
 */

import { useState } from 'react';

import { Notifiche } from '../components/notifications';
import { Stato } from '../components/state';
import { Spazio, Testata, Voce } from '../components/header';
import { Avviso, useFlusso } from '../flow-api';

interface Riga {
  articolo: string;
  sku: string;
  colore: string;
  capi: number;
}
interface Fermo {
  ordine: string;
  cliente: string;
  consegna: string;
  urgente: boolean;
  express: boolean;
  righe: Riga[];
}
interface VistaPrelievi {
  daPrelevare: Fermo[];
  piano: { ordine: string; cliente: string; bloccato: boolean; deroga: { chi: string } | null }[];
}

interface RigaExpress {
  variante: string;
  articolo: string;
  colore: string;
  taglia: string;
  ordinati: number;
  giaChiesti: number;
  inMagazzino: number;
  daChiedere: number;
  mancano: number;
}
type VistaExpress =
  | { esito: 'trovato'; ordine: string; giacenzaLetta: boolean; righe: RigaExpress[] }
  | { esito: 'non-trovato' | 'deconetwork-muto'; ordine: string };

/** One express order: what the Modula holds, what is missing, one button. */
function SchedaExpress({ ordine }: { ordine: string }) {
  const percorso = `/prelievi/express/${encodeURIComponent(ordine)}`;
  const { dati, caricando, ultimo, inCorso, fai } = useFlusso<VistaExpress>(
    `prelievi-express-${ordine}`,
    percorso,
  );
  if (caricando || !dati) return <p className="mod__hint">Leggo l’ordine {ordine}…</p>;

  if (dati.esito !== 'trovato') {
    return (
      <p className="mod__hint">
        {dati.esito === 'non-trovato'
          ? `DecoNetwork non ha un ordine ${ordine}: controlla il numero.`
          : 'DecoNetwork non risponde: riprova fra poco.'}
      </p>
    );
  }

  const capi = dati.righe.reduce((n, r) => n + r.daChiedere, 0);
  return (
    <article className="riga riga--att">
      <div className="riga__cliente">ordine express {dati.ordine}</div>
      {!dati.giacenzaLetta && (
        <p className="mod__hint">
          Il Modula non ha detto cosa contiene: finché non risponde non si chiede niente.
        </p>
      )}
      {dati.righe.map((r) => (
        <div key={r.variante || 'senza-codice'} style={{ marginTop: 8 }}>
          <div className="riga__conto">
            {r.variante === ''
              ? 'senza codice su DecoNetwork'
              : `${r.articolo} · ${r.colore} · ${r.taglia}`}{' '}
            ×{r.ordinati}
          </div>
          <div className="mod__hint">
            nel Modula {r.inMagazzino}
            {r.giaChiesti > 0 && ` · già chiesti ${r.giaChiesti}`}
            {r.daChiedere > 0 && ` · si chiedono ${r.daChiedere}`}
            {r.mancano > 0 && <b>{` · mancano ${r.mancano}`}</b>}
          </div>
        </div>
      ))}
      <div className="mods" style={{ marginTop: 10 }}>
        <button
          className="mod"
          disabled={inCorso || capi === 0}
          onClick={fai(`${percorso}/preleva`)}
        >
          Preleva quello che c’è · {capi} {capi === 1 ? 'capo' : 'capi'}
        </button>
      </div>
      <Avviso esito={ultimo} />
    </article>
  );
}

/** The number is typed, then confirmed: reading on every key would ask DecoNetwork for «6», «60»… */
function OrdineExpress() {
  const [scritto, setScritto] = useState('');
  const [ordine, setOrdine] = useState('');

  return (
    <div style={{ marginBottom: 16 }}>
      <form
        className="mods"
        style={{ alignItems: 'center' }}
        onSubmit={(e) => {
          e.preventDefault();
          setOrdine(scritto.trim());
        }}
      >
        <input
          className="cerca__campo"
          value={scritto}
          onChange={(e) => setScritto(e.target.value)}
          placeholder="numero ordine DecoNetwork"
          aria-label="numero ordine express"
        />
        <button className="mod" type="submit" disabled={scritto.trim() === ''}>
          Cerca
        </button>
      </form>
      {ordine !== '' && <SchedaExpress key={ordine} ordine={ordine} />}
    </div>
  );
}

export function Prelievi() {
  const { dati, caricando, ultimo, inCorso, fai } = useFlusso<VistaPrelievi>(
    'prelievi',
    '/prelievi',
  );
  if (caricando || !dati) return <Stato quale="caricando" dice="Cerco le righe da prelevare…" />;

  return (
    <div className="shell">
      <Testata>
        <Voce k="Prelievi da magazzino negozio" v={`${dati.daPrelevare.length} ordini fermi`} />
        <Spazio />
        <span className="mod__hint">
          La riga di prelievo la genera il sistema, non l’operatore.
        </span>
        <Notifiche />
      </Testata>

      {/*
        Una colonna sola: la cabina a sinistra conteneva solo spiegazioni — la
        postazione, la cassetta virtuale — e toglieva il 42% della larghezza agli
        articoli. Quelle frasi stanno nella Guida, «Ordini express e prelievi».
      */}
      <section className="righe righe--sola">
        <OrdineExpress />
        {dati.daPrelevare.length === 0 && (
          <p className="mod__hint">Nessun ordine fermo: tutto quello che serviva è stato preso.</p>
        )}
        {dati.daPrelevare.map((o) => (
          <article key={o.ordine} className={`riga riga--${o.urgente ? 'part' : 'att'}`}>
            <div className="riga__cliente">
              {o.cliente} {o.express && '· express'} {o.urgente && '· urgente'}
            </div>
            <div className="riga__sku">
              ordine {o.ordine} · consegna {o.consegna}
            </div>
            {o.righe.map((r) => (
              <div key={r.sku} style={{ marginTop: 8 }}>
                <div className="riga__conto">
                  {r.articolo} {r.colore} ×{r.capi}
                </div>
                <div className="mods" style={{ marginTop: 6 }}>
                  <button
                    className="mod"
                    disabled={inCorso}
                    onClick={fai('/prelievi/preleva', { ordine: o.ordine, sku: r.sku })}
                  >
                    Preleva
                  </button>
                  <button
                    className="mod mod--quiet"
                    disabled={inCorso}
                    onClick={fai('/prelievi/virtuale', { ordine: o.ordine, sku: r.sku })}
                  >
                    Cassetta virtuale
                  </button>
                </div>
              </div>
            ))}
            <div
              className="mods"
              style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--line)' }}
            >
              <button
                className="mod mod--fal"
                disabled={inCorso}
                onClick={fai(`/prelievi/${o.ordine}/deroga`)}
              >
                Autorizza il parziale
              </button>
              <span className="mod__hint">
                Solo l’amministratore, ed è nominativa: resta scritta sull’ordine fino alla
                spedizione.
              </span>
            </div>
          </article>
        ))}
      </section>

      <Avviso esito={ultimo} />
    </div>
  );
}
